import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const FIELD = "w-full bg-white border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]";
const LABEL = "block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2";
const BADGE = "px-2 py-1 text-[10px] uppercase tracking-[0.12em] border";
const JOB_STATUS = { open: "Offen", filled: "Besetzt", cancelled: "Abgesagt" };

const LAENDER = { DE: "Deutschland", AT: "Österreich" };

const EMPTY = {
  betrieb: "", plz: "", ort: "", land: "DE", strasse: "", taeglich_von: "", taeglich_bis: "",
  position: "", bereiche: [], tagessatz_eur: "", anzahl_koeche: 1, beschreibung: "", request_id: "",
};

export const JobBoard = ({ pwd, stamm, requests }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [candidates, setCandidates] = useState({});
  const [picked, setPicked] = useState({});
  const [edit, setEdit] = useState(null);
  const H = { headers: { "X-Admin-Password": pwd } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vermittlung/jobs`, { headers: { "X-Admin-Password": pwd } });
      setJobs(r.data);
    } catch {
      toast.error("Einsätze konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const prefill = (reqId) => {
    const r = requests.find((x) => x.id === reqId);
    if (!r) return setForm((f) => ({ ...f, request_id: "" }));
    setForm((f) => ({
      ...f, request_id: r.id, betrieb: r.betrieb, plz: r.plz, ort: r.ort, land: r.land || "DE",
      strasse: r.strasse || "",
      taeglich_von: r.taeglich_von || "", taeglich_bis: r.taeglich_bis || "", position: r.position || "",
      bereiche: r.bereiche || [], anzahl_koeche: r.anzahl_koeche || 1, beschreibung: r.beschreibung || "",
    }));
    const out = [];
    if (r.von_datum && r.bis_datum) {
      const d = new Date(r.von_datum), end = new Date(r.bis_datum);
      while (d <= end && out.length < 60) {
        out.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
      }
    } else if (r.von_datum) out.push(String(r.von_datum).slice(0, 10));
    setDates(out);
  };

  const createJob = async () => {
    if (!form.betrieb.trim() || !form.plz.trim() || !dates.length)
      return toast.error("Betrieb, PLZ und mindestens ein Einsatztag sind nötig");
    try {
      const body = { ...form, dates, tagessatz_eur: form.tagessatz_eur ? Number(form.tagessatz_eur) : null,
        anzahl_koeche: Number(form.anzahl_koeche) || 1 };
      const r = await axios.post(`${API}/admin/vermittlung/jobs`, body, H);
      toast.success(`Einsatz ${r.data.job_number} angelegt`);
      setForm(EMPTY); setDates([]); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Einsatz konnte nicht angelegt werden");
    }
  };

  const loadCandidates = async (job) => {
    try {
      const r = await axios.get(`${API}/admin/vermittlung/jobs/${job.id}/kandidaten`, H);
      setCandidates((c) => ({ ...c, [job.id]: r.data.kandidaten }));
      if (!r.data.kandidaten.length) toast.info("Kein Koch ist an allen Tagen frei bzw. im Umkreis");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kandidaten konnten nicht geladen werden");
    }
  };

  const togglePick = (jobId, chefId) =>
    setPicked((p) => {
      const cur = new Set(p[jobId] || []);
      cur.has(chefId) ? cur.delete(chefId) : cur.add(chefId);
      return { ...p, [jobId]: [...cur] };
    });

  const invite = async (job) => {
    const ids = picked[job.id] || [];
    if (!ids.length) return toast.error("Bitte mindestens einen Koch anhaken");
    const withMail = window.confirm(
      `Einsatz ${job.job_number} an ${ids.length} Koch/Köche senden?\n\n` +
      `OK = E-Mail mit persönlichem Zusage-Link verschicken\n` +
      `Abbrechen = nur vormerken, ohne E-Mail (du fragst selbst an)`
    );
    try {
      const r = await axios.post(
        `${API}/admin/vermittlung/jobs/${job.id}/einladen${withMail ? "" : "?notify=false"}`,
        { chef_ids: ids }, H);
      toast.success(withMail
        ? `Anfrage an ${r.data.invited.length} Koch/Köche versandt`
        : `${r.data.invited.length} Koch/Köche vorgemerkt — ohne E-Mail`);
      if (r.data.failed?.length) toast.error(r.data.failed.join("; "));
      setPicked((p) => ({ ...p, [job.id]: [] }));
      setCandidates((c) => ({ ...c, [job.id]: undefined }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const setInvite = async (job, chefId, body, label) => {
    try {
      await axios.post(`${API}/admin/vermittlung/jobs/${job.id}/invites/${chefId}`, body, H);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Änderung fehlgeschlagen");
    }
  };

  const removeInvite = async (job, inv) => {
    if (!window.confirm(`${inv.chef_name} aus diesem Einsatz entfernen?\n\nEs wird keine E-Mail verschickt.`)) return;
    try {
      await axios.delete(`${API}/admin/vermittlung/jobs/${job.id}/invites/${inv.chef_id}`, H);
      toast.success("Aus dem Einsatz entfernt");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Entfernen fehlgeschlagen");
    }
  };

  const unselect = async (job) => {
    if (!window.confirm("Einteilung aufheben?\n\nDer Einsatz geht zurück auf „offen“. Es wird KEINE E-Mail verschickt — den Koch informierst du am besten selbst.")) return;
    try {
      await axios.post(`${API}/admin/vermittlung/jobs/${job.id}/auswahl-aufheben`, {}, H);
      toast.success("Einteilung aufgehoben (ohne E-Mail)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aufheben fehlgeschlagen");
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Einsatz ${job.job_number} (${job.betrieb}) endgültig löschen?\n\nDas lässt sich nicht rückgängig machen.`)) return;
    try {
      await axios.delete(`${API}/admin/vermittlung/jobs/${job.id}`, H);
      toast.success("Einsatz gelöscht");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const startEdit = (job) => {
    setEdit({
      id: job.id, betrieb: job.betrieb, plz: job.plz, ort: job.ort, land: job.land || "DE",
      strasse: job.strasse || "",
      taeglich_von: job.taeglich_von || "", taeglich_bis: job.taeglich_bis || "",
      tagessatz_eur: job.tagessatz_eur ?? "", anzahl_koeche: job.anzahl_koeche || 1,
      beschreibung: job.beschreibung || "", dates: [...(job.dates || [])],
    });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/admin/vermittlung/jobs/${edit.id}`, {
        ...edit, tagessatz_eur: edit.tagessatz_eur === "" ? null : Number(edit.tagessatz_eur),
        anzahl_koeche: Number(edit.anzahl_koeche) || 1,
      }, H);
      toast.success("Einsatz geändert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const select = async (job, chefIds) => {
    const withMail = window.confirm(
      `Einsatz ${job.job_number} vergeben?\n\n` +
      `OK = Bestätigung an den Gewählten + freundliche Absage an alle anderen Zusagen\n` +
      `Abbrechen = nur eintragen, ohne jede E-Mail (du informierst selbst)`
    );
    try {
      const r = await axios.post(
        `${API}/admin/vermittlung/jobs/${job.id}/auswahl${withMail ? "" : "?notify=false"}`,
        { chef_ids: chefIds }, H);
      toast.success(withMail ? "Einsatz vergeben — Mails sind raus" : "Einsatz vergeben — ohne E-Mail");
      if (r.data.mail_warnings?.length) toast.error(r.data.mail_warnings.join("; "));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auswahl fehlgeschlagen");
    }
  };

  const followup = async (job) => {
    if (!window.confirm("Nachfass-Mail an den eingeteilten Koch (und den Betrieb) senden?\n\nFragt nach Zufriedenheit und ob weitere Termine geplant sind — dokumentiert den Kundenschutz.")) return;
    try {
      const r = await axios.post(`${API}/admin/vermittlung/jobs/${job.id}/nachfassen`, {}, H);
      toast.success(`Nachfrage versandt an: ${r.data.sent.join(", ") || "—"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nachfassen fehlgeschlagen");
    }
  };

  const setStatus = async (job, status) => {
    try {
      await axios.patch(`${API}/admin/vermittlung/jobs/${job.id}`, { status }, H);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  return (
    <div data-testid="jobboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-[#757067]">
          Einsatz anlegen → passende Köche auswählen → sie sagen per persönlichem Link zu → du vergibst.
        </p>
        <button onClick={() => setShowForm((s) => !s)} data-testid="jobboard-toggle-form"
          className="bg-[#2C2A26] text-white hover:bg-[#1f1d1a] px-5 py-3 text-[11px] uppercase tracking-[0.15em]">
          {showForm ? "Formular schließen" : "+ Einsatz anlegen"}
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-[#E3DECE] p-6 mb-8" data-testid="jobboard-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className={LABEL}>Aus Vermittlungsanfrage übernehmen (optional)</label>
              <select className={FIELD} value={form.request_id} onChange={(e) => prefill(e.target.value)}
                data-testid="jobboard-request-select">
                <option value="">— manuell anlegen —</option>
                {requests.filter((r) => r.placement_type === "temporary").map((r) => (
                  <option key={r.id} value={r.id}>{r.request_number} · {r.betrieb} · {fmt(r.von_datum)}</option>
                ))}
              </select>
            </div>
            <div><label className={LABEL}>Betrieb *</label>
              <input className={FIELD} value={form.betrieb} data-testid="jobboard-betrieb"
                onChange={(e) => setField("betrieb", e.target.value)} /></div>
            <div><label className={LABEL}>Land</label>
              <select className={FIELD} value={form.land} data-testid="jobboard-land"
                onChange={(e) => setField("land", e.target.value)}>
                {Object.entries(LAENDER).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className={LABEL}>PLZ * ({form.land === "AT" ? "4" : "5"}-stellig)</label>
              <input className={FIELD} value={form.plz} data-testid="jobboard-plz"
                onChange={(e) => setField("plz", e.target.value)} /></div>
            <div><label className={LABEL}>Ort</label>
              <input className={FIELD} value={form.ort} data-testid="jobboard-ort"
                onChange={(e) => setField("ort", e.target.value)} /></div>
            <div className="sm:col-span-3"><label className={LABEL}>Straße (erst nach Zusage sichtbar)</label>
              <input className={FIELD} value={form.strasse}
                onChange={(e) => setField("strasse", e.target.value)} /></div>
            <div><label className={LABEL}>Von (Uhrzeit)</label>
              <input className={FIELD} value={form.taeglich_von} placeholder="16:00"
                onChange={(e) => setField("taeglich_von", e.target.value)} /></div>
            <div><label className={LABEL}>Bis (Uhrzeit)</label>
              <input className={FIELD} value={form.taeglich_bis} placeholder="23:00"
                onChange={(e) => setField("taeglich_bis", e.target.value)} /></div>
            <div><label className={LABEL}>Tagessatz (€ netto)</label>
              <input type="number" step="10" className={FIELD} value={form.tagessatz_eur}
                data-testid="jobboard-tagessatz"
                onChange={(e) => setField("tagessatz_eur", e.target.value)} /></div>
            <div><label className={LABEL}>Position</label>
              <select className={FIELD} value={form.position} onChange={(e) => setField("position", e.target.value)}>
                <option value="">— beliebig —</option>
                {Object.entries(stamm.positionen || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className={LABEL}>Anzahl Köche</label>
              <input type="number" min="1" className={FIELD} value={form.anzahl_koeche}
                onChange={(e) => setField("anzahl_koeche", e.target.value)} /></div>
            <div className="sm:col-span-3">
              <label className={LABEL}>Bereiche</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stamm.bereiche || {}).map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setField("bereiche",
                    form.bereiche.includes(k) ? form.bereiche.filter((b) => b !== k) : [...form.bereiche, k])}
                    className={`px-3 py-2 text-xs border transition-colors ${form.bereiche.includes(k)
                      ? "bg-[#BA6D52] text-white border-[#BA6D52]" : "bg-white border-[#E3DECE] hover:border-[#BA6D52]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-3">
              <label className={LABEL}>Einsatztage *</label>
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" className={`${FIELD} w-auto`} value={newDate} data-testid="jobboard-date-input"
                  onChange={(e) => setNewDate(e.target.value)} />
                <button type="button" data-testid="jobboard-date-add"
                  onClick={() => { if (newDate && !dates.includes(newDate)) setDates([...dates, newDate].sort()); setNewDate(""); }}
                  className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                  + Tag
                </button>
                {dates.map((d) => (
                  <span key={d} className="inline-flex items-center gap-2 bg-[#F2EFE9] px-3 py-2 text-xs">
                    {fmt(d)}
                    <button type="button" onClick={() => setDates(dates.filter((x) => x !== d))}
                      className="text-[#BA6D52]">×</button>
                  </span>
                ))}
              </div>
            </div>
            <div className="sm:col-span-3">
              <label className={LABEL}>Beschreibung für den Koch</label>
              <textarea rows={3} className={FIELD} value={form.beschreibung}
                onChange={(e) => setField("beschreibung", e.target.value)}
                placeholder="z. B. à la carte, 60 Couverts, Posten Entremetier, eigene Kochjacke mitbringen" />
            </div>
          </div>
          <button onClick={createJob} data-testid="jobboard-create"
            className="mt-6 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-4 text-[11px] uppercase tracking-[0.2em]">
            Einsatz anlegen
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-[#757067]">Lade …</p>}
      {!loading && jobs.length === 0 && <p className="text-sm text-[#757067]">Noch keine Einsätze angelegt.</p>}

      <div className="space-y-4">
        {jobs.map((j) => {
          const yes = (j.invites || []).filter((i) => i.response === "yes");
          const no = (j.invites || []).filter((i) => i.response === "no");
          const open = (j.invites || []).filter((i) => !i.response);
          const chosen = (j.invites || []).filter((i) => i.selected);
          const cands = candidates[j.id];
          return (
            <div key={j.id} className="bg-white border border-[#E3DECE] p-6" data-testid={`job-card-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-xl">{j.betrieb}</h3>
                    <span className={`${BADGE} ${j.status === "filled" ? "border-green-700 text-green-800 bg-green-50"
                      : j.status === "cancelled" ? "border-[#E3DECE] text-[#757067]" : "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"}`}>
                      {JOB_STATUS[j.status] || j.status}
                    </span>
                    <span className="text-xs text-[#757067]">{j.job_number}</span>
                  </div>
                  <p className="text-sm text-[#757067] mt-1">
                    {j.customer_type === "private" ? "Privatkunde · " : ""}
                    {j.plz} {j.ort}{j.land === "AT" ? " (AT)" : ""} · {(j.dates || []).map(fmt).join(" · ")}
                    {j.taeglich_von ? ` · ${j.taeglich_von}–${j.taeglich_bis}` : ""}
                  </p>
                  <p className="text-sm text-[#757067]">
                    {j.tagessatz_eur ? `${j.tagessatz_eur.toFixed(2)} € netto/Tag · ` : ""}
                    {j.anzahl_koeche} Koch/Köche gesucht
                  </p>
                </div>
                <div className="text-right text-xs text-[#757067]">
                  <p>{(j.invites || []).length} angefragt</p>
                  <p className="text-green-800">{yes.length} Zusagen</p>
                  <p>{no.length} Absagen · {open.length} offen</p>
                </div>
              </div>

              {(j.invites || []).length > 0 && (
                <div className="mt-5 pt-5 border-t border-[#E3DECE] space-y-2">
                  {(j.invites || []).map((i) => (
                    <div key={i.chef_id} className="flex flex-wrap items-center gap-3 text-sm"
                      data-testid={`job-invite-${j.id}-${i.chef_id}`}>
                      <span className={`${BADGE} ${i.selected ? "border-green-700 text-green-800 bg-green-50"
                        : i.response === "yes" ? "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"
                        : i.response === "no" ? "border-[#E3DECE] text-[#757067]" : "border-[#E3DECE] text-[#757067]"}`}>
                        {i.selected ? "eingeteilt" : i.response === "yes" ? "Zusage" : i.response === "no" ? "Absage" : "offen"}
                      </span>
                      <span className="text-[#2C2A26]">{i.chef_name}</span>
                      {i.distance_km != null && <span className="text-[#757067] text-xs">{i.distance_km} km</span>}
                      {i.tagessatz_eur ? <span className="text-[#757067] text-xs">Wunsch: {i.tagessatz_eur.toFixed(2)} €</span> : null}
                      {i.note ? <span className="text-[#757067] text-xs">„{i.note}“</span> : null}
                      {j.note ? null : null}
                      {j.status === "open" && i.response === "yes" && (
                        <button onClick={() => select(j, [i.chef_id])} data-testid={`job-select-${j.id}-${i.chef_id}`}
                          className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-1.5 text-[10px] uppercase tracking-[0.15em]">
                          Diesen einteilen
                        </button>
                      )}
                      {!i.response && (
                        <>
                          <button onClick={() => setInvite(j, i.chef_id, { response: "yes" }, `Zusage von ${i.chef_name} eingetragen`)}
                            data-testid={`job-manual-yes-${j.id}-${i.chef_id}`}
                            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">
                            ✓ hat telefonisch zugesagt
                          </button>
                          <button onClick={() => setInvite(j, i.chef_id, { response: "no" }, `Absage von ${i.chef_name} eingetragen`)}
                            data-testid={`job-manual-no-${j.id}-${i.chef_id}`}
                            className="border border-[#E3DECE] text-[#757067] hover:border-[#2C2A26] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">
                            hat abgesagt
                          </button>
                        </>
                      )}
                      {i.response && !i.selected && (
                        <button onClick={() => setInvite(j, i.chef_id, { response: "" }, "Antwort zurückgesetzt")}
                          data-testid={`job-manual-reset-${j.id}-${i.chef_id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">
                          ↺ Antwort korrigieren
                        </button>
                      )}
                      <button onClick={() => removeInvite(j, i)} data-testid={`job-invite-remove-${j.id}-${i.chef_id}`}
                        className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">
                        entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 pt-5 border-t border-[#E3DECE] flex flex-wrap gap-3">
                <button onClick={() => (edit?.id === j.id ? setEdit(null) : startEdit(j))}
                  data-testid={`job-edit-${j.id}`}
                  className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                  {edit?.id === j.id ? "Bearbeiten schließen" : "✎ Einsatz bearbeiten"}
                </button>
                {j.status === "open" && (
                  <button onClick={() => loadCandidates(j)} data-testid={`job-candidates-${j.id}`}
                    className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                    Passende Köche finden
                  </button>
                )}
                {chosen.length > 0 && (
                  <>
                    <button onClick={() => followup(j)} data-testid={`job-followup-${j.id}`}
                      className="border border-[#E3DECE] text-[#575349] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      {j.followup_sent_at ? "↻ Nochmal nachfassen" : "Nachfassen (Folgetermine?)"}
                    </button>
                    <button onClick={() => unselect(j)} data-testid={`job-unselect-${j.id}`}
                      className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      ↺ Einteilung aufheben
                    </button>
                  </>
                )}
                {j.status === "open" && (
                  <button onClick={() => setStatus(j, "cancelled")} data-testid={`job-cancel-${j.id}`}
                    className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                    Einsatz absagen
                  </button>
                )}
                {j.status !== "open" && (
                  <button onClick={() => setStatus(j, "open")} data-testid={`job-reopen-${j.id}`}
                    className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                    Wieder öffnen
                  </button>
                )}
                <button onClick={() => removeJob(j)} data-testid={`job-delete-${j.id}`}
                  className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                  Löschen
                </button>
              </div>

              {edit?.id === j.id && (
                <div className="mt-5 pt-5 border-t border-[#E3DECE] bg-[#FAF5F1] p-5" data-testid={`job-edit-form-${j.id}`}>
                  <p className={LABEL}>Alles hier lässt sich jederzeit korrigieren</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[["betrieb", "Betrieb"], ["plz", "PLZ"], ["ort", "Ort"], ["strasse", "Straße"],
                      ["taeglich_von", "Von (Uhrzeit)"], ["taeglich_bis", "Bis (Uhrzeit)"]].map(([k, l]) => (
                      <div key={k}>
                        <label className={LABEL}>{l}</label>
                        <input className={FIELD} value={edit[k]} data-testid={`job-edit-${k}-${j.id}`}
                          onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
                      </div>
                    ))}
                    <div>
                      <label className={LABEL}>Land</label>
                      <select className={FIELD} value={edit.land} data-testid={`job-edit-land-${j.id}`}
                        onChange={(e) => setEdit({ ...edit, land: e.target.value })}>
                        {Object.entries(LAENDER).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={LABEL}>Tagessatz (€ netto)</label>
                      <input type="number" step="10" className={FIELD} value={edit.tagessatz_eur}
                        onChange={(e) => setEdit({ ...edit, tagessatz_eur: e.target.value })} />
                    </div>
                    <div>
                      <label className={LABEL}>Anzahl Köche</label>
                      <input type="number" min="1" className={FIELD} value={edit.anzahl_koeche}
                        onChange={(e) => setEdit({ ...edit, anzahl_koeche: e.target.value })} />
                    </div>
                    <div className="sm:col-span-3">
                      <label className={LABEL}>Einsatztage</label>
                      <div className="flex flex-wrap items-center gap-2">
                        <input type="date" className={`${FIELD} w-auto`} value={newDate}
                          onChange={(e) => setNewDate(e.target.value)} />
                        <button type="button" data-testid={`job-edit-date-add-${j.id}`}
                          onClick={() => { if (newDate && !edit.dates.includes(newDate)) setEdit({ ...edit, dates: [...edit.dates, newDate].sort() }); setNewDate(""); }}
                          className="border border-[#BA6D52] text-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          + Tag
                        </button>
                        {edit.dates.map((d) => (
                          <span key={d} className="inline-flex items-center gap-2 bg-white border border-[#E3DECE] px-3 py-2 text-xs">
                            {fmt(d)}
                            <button type="button" onClick={() => setEdit({ ...edit, dates: edit.dates.filter((x) => x !== d) })}
                              className="text-[#BA6D52]">×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-3">
                      <label className={LABEL}>Beschreibung für den Koch</label>
                      <textarea rows={2} className={FIELD} value={edit.beschreibung}
                        onChange={(e) => setEdit({ ...edit, beschreibung: e.target.value })} />
                    </div>
                  </div>
                  <div className="mt-5 flex gap-3">
                    <button onClick={saveEdit} data-testid={`job-edit-save-${j.id}`}
                      className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 py-3 text-[10px] uppercase tracking-[0.15em]">
                      Änderungen speichern
                    </button>
                    <button onClick={() => setEdit(null)}
                      className="border border-[#E3DECE] text-[#757067] px-6 py-3 text-[10px] uppercase tracking-[0.15em]">
                      Abbrechen
                    </button>
                  </div>
                  <p className="text-xs text-[#757067] mt-3">
                    Änderungen werden nur gespeichert — es geht keine E-Mail an Koch oder Betrieb raus.
                    Informiere sie bitte selbst, wenn sich Datum oder Zeiten ändern.
                  </p>
                </div>
              )}

              {cands && (
                <div className="mt-5 pt-5 border-t border-[#E3DECE]" data-testid={`job-candidate-list-${j.id}`}>
                  <p className={LABEL}>
                    {cands.length} Koch/Köche sind an allen Tagen frei und im Umkreis
                  </p>
                  <div className="space-y-2">
                    {cands.map((c) => (
                      <label key={c.chef_id} className={`flex flex-wrap items-center gap-3 p-3 border cursor-pointer text-sm transition-colors ${
                        (picked[j.id] || []).includes(c.chef_id) ? "border-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE] hover:border-[#BA6D52]"}`}
                        data-testid={`job-cand-${j.id}-${c.chef_id}`}>
                        <input type="checkbox" className="accent-[#BA6D52]" disabled={c.already_invited}
                          checked={(picked[j.id] || []).includes(c.chef_id)}
                          onChange={() => togglePick(j.id, c.chef_id)} />
                        <span className="text-[#2C2A26] font-medium">{c.name}</span>
                        <span className="text-[#757067] text-xs">
                          {c.sucht_privatkunden ? "Private Dining ✓ · " : ""}
                          {c.plz} {c.ort}{c.land === "AT" ? " (AT)" : ""}{c.distance_km != null ? ` · ${c.distance_km} km` : " · Entfernung unbekannt"}
                          {` · ${c.positionen || "—"}`}
                          {c.tagessatz_ab_eur ? ` · ab ${c.tagessatz_ab_eur.toFixed(0)} €/Tag` : ""}
                        </span>
                        {c.position_match && <span className={`${BADGE} border-[#BA6D52] text-[#BA6D52]`}>Position passt</span>}
                        {c.bereich_overlap > 0 && <span className={`${BADGE} border-[#E3DECE] text-[#757067]`}>{c.bereich_overlap}× Bereich</span>}
                        {c.already_invited && <span className="text-xs text-[#757067]">bereits angefragt</span>}
                      </label>
                    ))}
                  </div>
                  {cands.length > 0 && (
                    <button onClick={() => invite(j)} data-testid={`job-invite-send-${j.id}`}
                      className="mt-4 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 py-3 text-[10px] uppercase tracking-[0.15em]">
                      Anfrage an {(picked[j.id] || []).length} Koch/Köche senden
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
